import { LocationMarkerIcon } from "@heroicons/react/outline";
import { ChevronDownIcon, TrashIcon } from "@heroicons/react/solid";
import produce from "immer";
import { find, get, head, isEmpty, values } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useEffect, useMemo } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import create from "zustand";
import { persist } from "zustand/middleware";
import AlertMessage from "../../../components/AlertMessage";
import { PLACEMENT_DISTANCE_LIMIT } from "../../../components/Dashboard";
import DotsFallback from "../../../components/DotsFallback";
import ExternalLink from "../../../components/ExternalLink";
import ExternalLinkIcon from "../../../components/icons/ExternalLinkIcon";
import InfoIcon from "../../../components/icons/InfoIcon";
import { useAuthentication } from "../../../context/authentication";
import { useBillingPlans } from "../../../context/billingPlans/BillingPlansProvider";
import { useHeliumData } from "../../../context/heliumData";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLocations } from "../../../context/locations";
import { usePlacements } from "../../../context/placements";
import { useToast } from "../../../context/toast";
import { useTransmitScales } from "../../../context/transmitScales";
import { useWorkspaces } from "../../../context/workspaces/WorkspacesProvider";
import classNames from "../../../utils/classNames";
import { Entity } from "../../../utils/constants";
import {
  ClassificationValue,
  formatHotspotName,
  getClassification
} from "../../../utils/hotspotUtils";
import { PlanType } from "../../../utils/types";
import { Coordinates } from "../../../utils/useMapCenter";
import useMapConfig from "../../../utils/useMapConfig";
import useTransmitScaleConfig from "../../../utils/useTransmitScaleConfig";
import { isActiveInstall } from "../../Workspace/InstallsData/utils";
import StatisticsCardWithRelativePerformance from "./StatisticsCardWithRelativePerformance";

const RewardScalingTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { chainVars } = useHeliumData();
  const showPocExplanation = usePocExplanation(
    (state) => state.showPocExplanation
  );
  const toggleShowPocExplanation = usePocExplanation(
    (state) => state.toggleShowPocExplanation
  );
  const { setModalUpgradeConfig } = useToast();
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const {
    currentHotspot,
    hotspotWitnessesCountsMap,
    isNotFetchedInLastMinute,
    fetchWitnessesCountsByHotspots,
    lastFetched,
    getHotspotCoordinates
  } = useHotspots();
  const { currentWorkspace } = useWorkspaces();
  const { wrapActionWithAuth } = useAuthentication();
  const {
    isInteractiveHotspot,
    calculatedTransmitScales,
    getTransmitScaleInfo,
    getHexTransmitScaleInfo,
    isTransmitScaleActive
  } = useTransmitScales();
  const { getPlanByType, setNewPlan } = useBillingPlans();
  const forcedInteractiveIds = useTransmitScaleConfig(
    (state) => state.forcedInteractiveIds
  );
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );
  const toggleForceInteractivity = useTransmitScaleConfig(
    (state) => state.toggleForceInteractivity
  );
  const { installs } = useInstalls();
  const { locations, setSelectPointsOnMapConfig, getLocationCoordinates } =
    useLocations();
  const { placements, removePlacement } = usePlacements();

  const hip15WitnessRedundancy = useMemo(
    () => get(chainVars, "witnessRedundancy", 4),
    [chainVars]
  );

  const pocPerHopMaxWitnesses = useMemo(
    () => get(chainVars, "pocPerHopMaxWitnesses", 14),
    [chainVars]
  );

  const currentLocation = useMemo(
    () =>
      props.match.params.id ? locations[props.match.params.id] : undefined,
    [locations, props.match.params.id]
  );

  const entity = useMemo(
    () => (currentLocation ? Entity.location : Entity.hotspot),
    [currentLocation]
  );

  const currentPlacement = useMemo(
    () => placements[props.match.params.id],
    [placements, props.match.params.id]
  );

  const currentCoordinates: Coordinates | undefined = useMemo(() => {
    if (currentPlacement) return [currentPlacement.lng, currentPlacement.lat];
    if (currentHotspot) return [currentHotspot.lng, currentHotspot.lat];
    if (currentLocation) return [currentLocation.lng, currentLocation.lat];
    return;
  }, [currentHotspot, currentLocation, currentPlacement]);

  const isActive = useMemo(
    () => isTransmitScaleActive(props.match.params.id),
    [isTransmitScaleActive, props.match.params.id]
  );

  const linkedActiveLocationInstall = useMemo(() => {
    if (!currentLocation) return;

    return find(values(installs).filter(isActiveInstall), {
      location_uuid: currentLocation.uuid
    });
  }, [currentLocation, installs]);

  const isForcedInteractive = useMemo(
    () => forcedInteractiveIds.includes(props.match.params.id),
    [forcedInteractiveIds, props.match.params.id]
  );

  const transmitScaleInfo = useMemo(() => {
    let coordinates: { h3Index: string; lat: number; lng: number } | undefined;

    if (currentLocation) {
      coordinates = getLocationCoordinates(currentLocation.uuid);
    }

    if (currentHotspot) {
      coordinates = getHotspotCoordinates(currentHotspot.id);
    }

    return coordinates ? getTransmitScaleInfo(coordinates.h3Index) : null;
  }, [
    currentHotspot,
    currentLocation,
    getHotspotCoordinates,
    getLocationCoordinates,
    getTransmitScaleInfo
  ]);

  const calculatedTransmitScale = useMemo(
    () => calculatedTransmitScales[props.match.params.id] || 0,
    [calculatedTransmitScales, props.match.params.id]
  );

  const witnessRewardUnits = useMemo(
    () => get(currentHotspot, "rx_reward_unit", 0),
    [currentHotspot]
  );

  useEffect(() => {
    if (!currentHotspot) return;

    const now = Date.now().valueOf();

    const witnessesCountsFetchId = `hotspot-witnesses-counts-${currentHotspot.id}`;
    if (isNotFetchedInLastMinute(witnessesCountsFetchId)) {
      fetchWitnessesCountsByHotspots([currentHotspot.id]);
      lastFetched.current[witnessesCountsFetchId] = now;
    }
  }, [
    currentHotspot,
    fetchWitnessesCountsByHotspots,
    isNotFetchedInLastMinute,
    lastFetched
  ]);

  const renderNonInteractivityAlert = useMemo(() => {
    if (
      !currentHotspot ||
      isInteractiveHotspot(currentHotspot.last_poc_challenge)
    ) {
      return;
    }

    const hotspotName = formatHotspotName(currentHotspot);

    let message = `${hotspotName} is not interactive and is thus excluded from the transmit scale calculations. It needs to transmit a beacon to become interactive.`;

    let reason;

    if (!currentHotspot.online) {
      reason = "inactive";
    }

    if (reason) {
      message += ` The problem is likely that it is currently ${reason}.`;
    }

    return (
      <>
        {message}
        <div className="flex items-center mt-4 ">
          <button
            type="button"
            className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
              isForcedInteractive
                ? "bg-hotspotty-400 dark:bg-white"
                : "bg-gray-200"
            }`}
            aria-pressed="false"
            aria-labelledby="toggle-force-interactive"
            onClick={() => toggleForceInteractivity(props.match.params.id)}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
                isForcedInteractive ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
          <div className="ml-2">Simulate interactivity</div>
        </div>
      </>
    );
  }, [
    currentHotspot,
    isForcedInteractive,
    isInteractiveHotspot,
    props.match.params.id,
    toggleForceInteractivity
  ]);

  const renderIsActiveSection = useMemo(
    () => (
      <div className="mt-6">
        <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Metrics
          </h3>
        </div>

        <span className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
          {renderNonInteractivityAlert}

          {calculatedTransmitScale * witnessRewardUnits > 0 &&
            (calculatedTransmitScale * witnessRewardUnits === 1
              ? "Most rewards come from witnessing activities, which are usually decreased based on the values below. For this hotspot the multipliers are as good as they can get which means that there is room for more hotspots in this area."
              : "Most rewards come from witnessing activities, which are decreased based on the values below. The lower these values, the less attractive it is to install a hotspot nearby because there is no added value for witnessing it.")}
        </span>

        <dl className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <StatisticsCardWithRelativePerformance
            id={props.match.params.id}
            entity={entity}
            title={
              <span className="truncate">
                <span className="capitalize mr-1">
                  {getClassification(calculatedTransmitScale)}
                </span>
                Transmit Scale
                <Tooltip
                  placement="top"
                  overlay={
                    <>
                      <ExternalLink
                        className="inline-flex text-xs mr-1"
                        text="HIP17"
                        url="https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md"
                      />
                      rewards hotspots transmitting from a given location based
                      on how many other hotspots can transmit from the same
                      area. It assigns a transmit scale which gets lower the
                      more saturated the area is so that a redundancy is met but
                      oversaturation is disincentivized. PoC rewards are
                      multiplied by the transmit scale of the transmitting
                      hotspot.
                    </>
                  }
                >
                  <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                </Tooltip>
              </span>
            }
            metricIteratee="reward_scale"
          >
            <ClassificationValue
              value={calculatedTransmitScale}
              hexProps={{
                width: 20,
                height: 22.8
              }}
            />
          </StatisticsCardWithRelativePerformance>

          {entity === Entity.hotspot && (
            <StatisticsCardWithRelativePerformance
              id={props.match.params.id}
              entity={Entity.hotspot}
              title={
                <span className="truncate">
                  <span className="capitalize mr-1">
                    {isEmpty(hotspotWitnessesCountsMap)
                      ? "Loading"
                      : witnessRewardUnits
                      ? getClassification(witnessRewardUnits)
                      : "Unknown"}
                  </span>
                  Witness Reward Units
                  <Tooltip
                    placement="top"
                    overlay={
                      <>
                        This represents the number of reward units another
                        hotspot gets for witnessing this hotspot. This value
                        ranges from 0.12 to 1 and is calculated by the 'Reward
                        Formula for each Receiver' in{" "}
                        <ExternalLink
                          className="inline-flex text-xs mr-1"
                          text="HIP15"
                          url="https://github.com/helium/HIP/blob/master/0015-beaconing-rewards.md"
                        />
                        . If this value is below 1, it means that this hotspot
                        can be witnessed by more than {hip15WitnessRedundancy}{" "}
                        other hotspots, which is the redundancy targeted by
                        Helium.
                      </>
                    }
                  >
                    <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                  </Tooltip>
                </span>
              }
              metricIteratee="rx_reward_unit"
            >
              <DotsFallback
                loading={
                  isEmpty(hotspotWitnessesCountsMap) || !witnessRewardUnits
                }
              >
                <ClassificationValue
                  value={witnessRewardUnits}
                  hexProps={{
                    width: 20,
                    height: 22.8
                  }}
                  withoutTransmitScaleTooltip
                />
              </DotsFallback>
            </StatisticsCardWithRelativePerformance>
          )}
        </dl>

        {!!transmitScaleInfo && (
          <div className="mt-6 pb-5">
            <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                HIP17 Transmit Scale - Diminishing rewards from saturated hexes
              </h3>
            </div>

            <span className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
              The HIP17 transmit scale is based on calculations for{" "}
              <ExternalLink
                className="inline-flex"
                text="h3 hexes"
                url="https://h3geo.org/docs/highlights/indexing"
              />{" "}
              at different resolutions.
              {!isEmpty(transmitScaleInfo.problematicResolutions) && (
                <span className="block">
                  The
                  {transmitScaleInfo.problematicResolutions.length === 1
                    ? ` hex at resolution ${head(
                        transmitScaleInfo.problematicResolutions
                      )} needs to be improved, `
                    : ` hexes at resolutions ${transmitScaleInfo.problematicResolutions
                        .reverse()
                        .join(", ")} need to be improved, `}
                  as
                  <span className="ml-1 text-red-500 dark:text-red-400">
                    highlighted
                  </span>{" "}
                  below.
                </span>
              )}{" "}
              The excess or available number of interactive hotspots or planned
              locations is displayed per hex below.
            </span>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3 xs:grid-cols-2">
              {values(transmitScaleInfo.parentHexes)
                .reverse()
                .map((h3Index) => {
                  const { resolution, transmitScale, densityStatusMessage } =
                    getHexTransmitScaleInfo(h3Index);

                  if (resolution > 10) return null; // This is because the chainVars for densityTarget are set to 100000 for resolutions 11 and 12

                  return (
                    <Link
                      key={h3Index}
                      to={`/hexes/${h3Index}/transmit-scale`}
                      className={classNames(
                        transmitScaleInfo.problematicResolutions.includes(
                          resolution
                        )
                          ? "border-2 border-red-300 hover:border-red-400 focus-within:ring-red-500 dark:border-red-400 dark:hover:border-red-500"
                          : "border border-gray-300 hover:border-gray-400 focus-within:ring-hotspotty-400 dark:hover:border-white",
                        "relative rounded-lg bg-white dark:bg-gray-800 px-6 py-5 shadow-sm flex items-center space-x-3 focus-within:ring-2 focus-within:ring-offset-2"
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <span className="absolute inset-0" aria-hidden="true" />
                        <p className="text-sm font-medium">
                          Resolution {resolution}
                        </p>
                        <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                          {densityStatusMessage}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <ClassificationValue
                          value={transmitScale}
                          hexProps={{
                            width: 20,
                            height: 22.8
                          }}
                        />
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    ),
    [
      renderNonInteractivityAlert,
      calculatedTransmitScale,
      witnessRewardUnits,
      props.match.params.id,
      entity,
      hotspotWitnessesCountsMap,
      hip15WitnessRedundancy,
      transmitScaleInfo,
      getHexTransmitScaleInfo
    ]
  );

  if (!currentHotspot && !currentLocation) return null;

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <span className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
        <a
          className="inline-flex items-center hover:text-hotspotty-500 truncate font-semibold dark:hover:text-white dark:hover:underline"
          href="https://docs.helium.com/blockchain/proof-of-coverage/#poc-reward-scaling"
          target="_blank"
          rel="noreferrer"
        >
          Reward scaling
          <ExternalLinkIcon className="h-4 w-4 inline ml-1" />
        </a>{" "}
        promotes installation of hotspots in areas with insufficient coverage
        and penalizes unnecessary duplicate coverage. Rewards for witnessing and
        being witnessed are reduced using formulas described by{" "}
        <a
          className="inline-flex items-center hover:text-hotspotty-500 truncate font-semibold dark:hover:text-white dark:hover:underline"
          href="https://github.com/helium/HIP/blob/master/0015-beaconing-rewards.md"
          target="_blank"
          rel="noreferrer"
        >
          HIP15
          <ExternalLinkIcon className="h-4 w-4 inline ml-1" />
        </a>{" "}
        and{" "}
        <a
          className="inline-flex items-center hover:text-hotspotty-500 truncate font-semibold dark:hover:text-white dark:hover:underline"
          href="https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md"
          target="_blank"
          rel="noreferrer"
        >
          HIP17
          <ExternalLinkIcon className="h-4 w-4 inline ml-1" />
        </a>
        .{" "}
        {!showPocExplanation && (
          <span
            className="hover:text-hotspotty-500 font-semibold cursor-pointer"
            onClick={toggleShowPocExplanation}
          >
            PoC rewards explained <ChevronDownIcon className="h-4 w-4 inline" />
          </span>
        )}
      </span>

      {showPocExplanation && (
        <AlertMessage
          className="mt-6"
          type="info"
          title="PoC rewards explained"
          message={
            <>
              Proof of Coverage challenges are exercises for hotspots to prove
              their usefulness to the network, which is designed to pass data
              between sensors and servers using RF.
              <br />
              <br />
              In a PoC challenge a hotspot transmits a beacon and upon
              successful witnessing by other hotspots, two things were achieved:
              <ul>
                <li>
                  1 - the transmitter proved that it can reach sensors and
                  deliver data to them
                </li>
                <li>
                  2 - the witnessing hotspots proved that they can receive and
                  publish data from sensors
                </li>
              </ul>
              <br />
              Rewards will be given to these hotspots but the question is: how
              much value did each hotspot actually provide?
              <br />
              <br />
              <a
                className="inline-flex items-center hover:text-hotspotty-500 truncate font-semibold"
                href="https://github.com/helium/HIP/blob/master/0015-beaconing-rewards.md"
                target="_blank"
                rel="noreferrer"
              >
                HIP15
                <ExternalLinkIcon className="h-4 w-4 inline ml-1" />
              </a>{" "}
              rewards up to {hip15WitnessRedundancy + 1} <b>reward units</b>
              <Tooltip
                placement="top"
                overlay="A reward unit is not a defined amount of HNT but a slice of the available PoC rewards for each epoch. So, for example if there are 1,000 HNT to be rewarded for PoC activity and there are 100 reward units from all PoC activity, each reward unit will get 10 HNT. If there are 5,000 reward units in an epoch, each unit will get 0.2 HNT."
              >
                <InfoIcon className="inline-flex h-4 w-4 mx-1 mb-1" />
              </Tooltip>
              for a PoC challenge:
              <ul>
                <li>
                  1 - the transmitting hotspot earns between 0 reward units (no
                  witnesses) and 1.99 reward units ({pocPerHopMaxWitnesses}+
                  witnesses).
                </li>
                <li>
                  2 - witnessing hotspots earn between 1 reward unit (up to{" "}
                  {hip15WitnessRedundancy} witnesses) and 0.12 reward units (
                  {pocPerHopMaxWitnesses}+ witnesses).
                </li>
              </ul>
              <br />
              Hotspotty displays the estimated Witness Reward Units (number 2
              above) for each hotspot. This facilitates prioritization of
              hotspot installs in areas where there's still value to witnessing
              the hotspots around (Witness Reward Units ~ 1.00).
              <br />
              <br />
              <a
                className="inline-flex items-center hover:text-hotspotty-500 truncate font-semibold"
                href="https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md"
                target="_blank"
                rel="noreferrer"
              >
                HIP17
                <ExternalLinkIcon className="h-4 w-4 inline ml-1" />
              </a>{" "}
              rewards hotspots transmitting from a given location based on how
              many other hotspots can transmit from the same area. It assigns a
              transmit scale which gets lower the more saturated the area is so
              that a redundancy is met but oversaturation is disincentivized.
              The abovementioned <b>reward units</b> are multiplied by the
              transmit scale of the transmitting hotspot.
              <br />
              <br />
              Most rewards are earnend by witnessing other hotspots, and these
              rewards are scaled down by the transmit scale of these other
              hotspots. This means that optimizing rewards (and thereby the
              network) comes down to collective network planning efforts in
              equally spreading out the network, as that will be more valuable
              as a whole and more profitable individually.
            </>
          }
          onClose={toggleShowPocExplanation}
        />
      )}

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Transmit scale simulation
            </h3>
          </div>

          <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
            Simulate the transmit scales for this area by enabling or disabling
            hotspots and/or locations.
          </p>

          {!!linkedActiveLocationInstall ? (
            <span className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
              Simulation is disabled for this location as there is currently{" "}
              <Link
                className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                to={`/installs/${linkedActiveLocationInstall.uuid}`}
              >
                a hotspot installed in this location
              </Link>
              .
            </span>
          ) : (
            <div className="mt-6 flex items-center mb-4 ">
              <button
                type="button"
                className={`ml-2 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
                  isActive
                    ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                    : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400"
                }`}
                aria-pressed="false"
                aria-labelledby="toggle-is-active"
                onClick={() => toggleIsActive(props.match.params.id)}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
                    isActive ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
              <div className="ml-2">Include in transmit scale simulation</div>
            </div>
          )}
        </div>

        <div>
          <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
              Placement simulation
              <Tooltip
                placement="top"
                overlay={
                  <>
                    Placements over{" "}
                    {renderDistance(PLACEMENT_DISTANCE_LIMIT, false)} away from
                    the physical install are not recommended as{" "}
                    <ExternalLink
                      className="inline-flex text-xs"
                      text="PoCv11"
                      url="https://blog.helium.com/pocv11-explained-call-to-action-4add36c75a1d"
                    />{" "}
                    will invalidate witnesses resulting in less rewards.
                  </>
                }
              >
                <InfoIcon className="inline-flex h-4 w-4 ml-1" />
              </Tooltip>
            </h3>
          </div>

          <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
            Simulate the transmit scale by positioning the placement on the map.
          </p>

          <button
            type="button"
            className={classNames(
              "mt-4 inline-flex items-center bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400",
              currentCoordinates &&
                "cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700"
            )}
            disabled={!currentCoordinates}
            onClick={wrapActionWithAuth(() => {
              if (!currentWorkspace?.permissions["transmit-scale-placements"]) {
                setModalUpgradeConfig({
                  open: true,
                  resolve: () => {
                    setNewPlan((prevState) => ({
                      ...prevState,
                      plan: getPlanByType(PlanType.pro)
                    }));
                  },
                  nextPlan: getPlanByType(PlanType.pro),
                  featureTitle: "Transmit scale placements",
                  featureDescription:
                    "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                });

                return;
              }

              setSelectPointsOnMapConfig({
                coordinates: currentCoordinates,
                placementContext: {
                  hotspot: currentHotspot,
                  location: currentLocation,
                  placement: currentPlacement,
                  redirectTo: props.location.pathname
                }
              });
            })}
          >
            <LocationMarkerIcon className="h-4 w-4 inline-flex mr-2" />
            Edit placement
          </button>

          {currentPlacement && (
            <button
              type="button"
              className="mt-4 ml-2 inline-flex items-center bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400 cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700"
              onClick={wrapActionWithAuth(() => {
                removePlacement(currentPlacement);
              })}
            >
              <TrashIcon className="h-4 w-4 inline-flex mr-2" />
              Remove placement
            </button>
          )}
        </div>
      </div>

      {isActive && renderIsActiveSection}
    </div>
  );
};

export default RewardScalingTab;

//
// Utils
//

type PocExplanationState = {
  showPocExplanation: boolean;
  toggleShowPocExplanation: () => void;
};

const usePocExplanation = create<PocExplanationState>(
  persist(
    (set, get) => ({
      showPocExplanation: true,
      toggleShowPocExplanation: () =>
        set((state) =>
          produce(state, (draft) => {
            draft.showPocExplanation = !state.showPocExplanation;
          })
        )
    }),
    {
      name: "poc_explanation"
    }
  )
);
